import { plainLabel } from './blocked';
import type { AgentScreen } from './screenIndex';

/**
 * 답변 끝의 「조회한 화면」 줄. **쓰는 사람은 사무직이다.**
 *
 * 화면 경로(`/w_tb_pb401`)도 state key 도 그 사람 말이 아니다. 여기서 세 가지를 더 고친다 —
 * 실제로 사용자가 읽고 「이거 왜 두 번 조회됐을까」라고 물어본 자리다(2026-09-17).
 *
 * ```
 * 전: 출장보고서 (기간 20260801, 기간 20260831, 담당자 00456),
 *     출장보고서 (기간 20260801, 기간 20260831, 담당자 00456)
 * 후: 출장보고서 (기간 2026-08-01~2026-08-31, 담당자 조태호)
 * ```
 *
 * 1. **같은 줄은 한 번만.** 목록을 부르고 그중 한 건을 다시 부르면 화면도 조건도 같은
 *    줄이 두 번 붙는다. 조건이 다르면(작년/올해) 그대로 둘 다 남긴다 — 그때는 정말로
 *    두 번 조회한 것이고 사용자가 알아야 한다.
 * 2. **이름이 같은 조건 둘은 기간이다.** `기간 20260801, 기간 20260831` 은 한 칸이
 *    아니라 시작·끝 두 칸인데 라벨이 같아 읽는 사람이 헷갈린다.
 * 3. **코드는 짝이 되는 이름이 함께 있으면 안 적는다.** `담당자 00456` 은 아무 뜻이 없다.
 */
export function readableSources(
  retrievals: { route: string; conditions: Record<string, string> }[],
  screens: AgentScreen[],
): string[] {
  const lines = retrievals.map((retrieval) => describe(retrieval, screens));
  return lines.filter((line, index) => lines.indexOf(line) === index);
}

/**
 * 계산 결과에 남은 근거(`/w_… (planmon=202610, state=전체)`)를 사람 말로. 보고서 머리 줄에 그대로 찍혔다(2026-10-23).
 * 그 꼴이 아닌 줄은 이미 사람 말이다 — 그대로 둔다.
 */
export function readableSourceLines(sources: string[], screens: AgentScreen[]): string[] {
  const lines = sources.map((line) => {
    const found = line.match(/^(\/\S+)(?:\s*\((.*)\))?$/);
    if (!found) return line;
    const conditions: Record<string, string> = {};
    (found[2] ?? '').split(/,\s*/).forEach((pair) => {
      const at = pair.indexOf('=');
      if (at > 0) conditions[pair.slice(0, at).trim()] = pair.slice(at + 1).trim();
    });
    return describe({ route: found[1] as string, conditions }, screens);
  });
  return lines.filter((line, index) => lines.indexOf(line) === index);
}

function describe(
  retrieval: { route: string; conditions: Record<string, string> },
  screens: AgentScreen[],
): string {
  const screen = screens.find((one) => one.route === retrieval.route);
  const labels = new Map((screen?.fields ?? [])
    .map((field) => [field.key, plainLabel(field.label ?? field.key)]));
  // 빈 조건은 걸지 않은 것이다. 적어 두면 `검색어 , 부서 ,` 처럼 뜻 없는 꼬리가 붙는다.
  const given = Object.entries(retrieval.conditions)
    .filter(([, value]) => String(value ?? '').trim());
  const kept = given.filter(([key]) => !hasName(key, given));
  const parts: string[] = [];
  const merged = new Set<number>();
  kept.forEach(([key, value], index) => {
    if (merged.has(index)) return;
    const label = labels.get(key) ?? key;
    const twin = kept.findIndex(([other], at) => at > index && (labels.get(other) ?? other) === label);
    if (twin >= 0) {
      merged.add(twin);
      parts.push(`${label} ${readable(value)}~${readable(kept[twin]?.[1] ?? '')}`);
      return;
    }
    parts.push(`${label} ${readable(value)}`);
  });
  const name = screen?.name ?? retrieval.route;
  return parts.length ? `${name} (${parts.join(', ')})` : name;
}

/** 이 코드 칸의 짝이 되는 이름 칸이 같은 조건에 함께 있나. */
function hasName(key: string, given: [string, string][]): boolean {
  const stem = key.replace(/(cd|id|code)$/i, '');
  if (stem === key) return false;
  return given.some(([other]) => other === `${stem}nm` || other === `${stem}name`);
}

/** 여덟 자리 날짜는 사람이 읽는 꼴로 적는다. 그 밖의 값은 그대로 둔다. */
function readable(value: string): string {
  const raw = String(value ?? '').trim();
  if (!/^\d{8}$/.test(raw)) return raw;
  return `${raw.slice(0, 4)}-${raw.slice(4, 6)}-${raw.slice(6)}`;
}
